"use client";

import { type MouseEvent, useState, type FC, type Ref } from "react";

import { useTranslation } from "react-i18next";

import { TextField, type TextFieldProps } from "../TextField/TextField";
import { Tooltip } from "../Tooltip";
import { IconButton } from "../IconButton";

export type PasswordFieldProps = Omit<TextFieldProps, "type"> & {
  /** Whether to show the password visibility toggle button. Defaults to `true`. */
  showToggle?: boolean;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * PasswordField is a component that wraps TextField with a password visibility toggle.
 */
export const PasswordField: FC<PasswordFieldProps> = ({
  showToggle = true,
  trailing,
  autoComplete = "current-password",
  ignorePasswordManagers = false,
  autoCapitalize = "none",
  spellCheck = false,
  ref,
  ...props
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const handleToggle = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible((prev) => !prev);
  };

  return (
    <TextField
      ref={ref}
      type={isVisible ? "text" : "password"}
      ignorePasswordManagers={ignorePasswordManagers}
      autoComplete={autoComplete}
      autoCapitalize={autoCapitalize}
      spellCheck={spellCheck}
      trailing={
        showToggle ? (
          <Tooltip
            value={
              isVisible ? t("passwordField.hide") : t("passwordField.show")
            }
          >
            <IconButton
              icon={isVisible ? "eyeOff" : "eye"}
              onClick={handleToggle}
              onMouseDown={(e) => e.preventDefault()}
              size="inline"
              variant="opaqueCurrentColor"
            />
          </Tooltip>
        ) : (
          trailing
        )
      }
      {...props}
    />
  );
};
